import axios from 'axios';

// Load API URL from config
let APIURL = '';

async function loadConfig() {
    if (APIURL) return;
    try {
        const response = await fetch('/config.json');
        const config = await response.json();
        APIURL = config.APIURL;
    } catch (error) {
        APIURL = 'http://localhost/invoicesnap'; // Fallback URL
    }
}

// Centralized redirect handler
function handleRedirect(store, condition, message, redirectUrl) {
    if (condition) {
        store.dispatch('logout');
        if (message) alert(message);
        window.location.href = redirectUrl;
        return true;
    }
    return false;
}

export async function defaultApiCall(options) {
    if (!APIURL) await loadConfig();

    const fullUrl = `${APIURL}${options.url}`;
    try {
        const response = await axios({
            method: options.method || 'GET',
            url: fullUrl,
            data: options.method === 'POST' ? options.data : undefined,
            params: options.method === 'GET' ? options.data : undefined,
            headers: options.headers || {},
        });
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.message || 
            error.request ? 'No response received from server' : 
            error.message || 'Request failed'
        );
    }
}

/**
 * @typedef {Object} ApiOptions
 * @property {string} url
 * @property {'GET'|'POST'|'PUT'|'DELETE'} [method]
 * @property {Object} [data]
 * @property {Object} [headers]
 * @property {number} [retries]
 * @property {number} [retryDelay]
 * @property {number} [timeout]
 * @property {AbortSignal} [abortSignal]
 */

/**
 * @param {ApiOptions} options
 * @param {Object} store
 * @returns {Promise<any>}
 */
export async function apiCall(options, store) {
    if (!APIURL) await loadConfig();

    const {
        url,
        method = 'GET',
        data = {},
        headers = {},
        retries = 2,
        retryDelay = 1000,
        timeout = 10000,
        abortSignal,
    } = options;

    if (!url) throw new Error('URL parameter is required');

    const makeRequest = async (attempt = 1) => {
        const controller = abortSignal ? null : new AbortController();
        try {
            const token = store.getters.getToken;
            const fullUrl = `${APIURL}${url}`;

            const response = await axios({
                method,
                url: fullUrl,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : undefined,
                    ...headers,
                },
                ...(method === 'GET' ? { params: { ...data, _t: Date.now() } } : { data }),
                timeout,
                signal: abortSignal || controller?.signal,
            });

            // Handle session/auth responses
            if (handleRedirect(store, response.data?.isLoggedIn === false, null, '/login')) return null;

            return response.data;
        } catch (error) {
            if (error.name === 'AbortError') {
                throw new Error('Request cancelled');
            }

            // Retry logic
            if (
                attempt < retries &&
                (error.code === 'ECONNABORTED' ||
                 !error.response ||
                 error.response.status >= 500)
            ) {
                await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
                return makeRequest(attempt + 1);
            }

            // Enhanced error handling
            const enhancedError = new Error(
                error.response?.data?.message || 
                error.message || 
                'Request failed'
            );
            enhancedError.status = error.response?.status;
            enhancedError.data = error.response?.data;
            enhancedError.originalError = error;
            throw enhancedError;
        }
    };

    return makeRequest();
}

// Utility functions
export function capitalizeFirstLetter(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function checkValidEmail(email) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email || !emailRegex.test(email)) {
        return false;
    }
    const domain = email.split('@').pop();
    const allowedEmailDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'aol.com', 'mail.com', 'msn.com', 'live.com'];
    return allowedEmailDomains.includes(domain);
}

export function validateMobile(mobile) {
    const regex = /^[6-9]\d{9}$/;
    return regex.test(mobile);
}